/**
 * Single-card evaluation flow when link has avaliar=PHONE but no shared group.
 * Fetches target via GET /evaluation/resolve/profile/:uuid, shows form, submits via POST /evaluation/feedback/global.
 */
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'

import * as Belt from '@goodfellas/graduation'

import { goodfellasApi } from '../../../apis'
import { normalizeBeltColor } from '../../../functions/graduation'
import { useFeedbackStore, useStore } from '../../../functions/store'
import { FighterType } from '../../../types/fighter'
import Sidebar from './Sidebar'
import FightersCards from './fighterCards'

const DEFAULT_POINTS_LEFT = 5

const defaultPersonalSkills = () => ({
  technique: 0,
  strength: 0,
  agility: 0,
  stamina: 0,
  commitment: 0,
  focus: 0,
})

const defaultGameSkills = () => ({
  guard: 0,
  guardRecovery: 0,
  guardPassing: 0,
  sweep: 0,
  takedown: 0,
  submission: 0,
  submissionDefense: 0,
  positionDefense: 0,
})

export function DashboardFeedbackByProfile({
  profileUserUuid,
}: {
  profileUserUuid: string
}) {
  const { t } = useTranslation('avaliacao360')
  const userStore = useStore((state) => state)
  const feedbackStore = useFeedbackStore((state) => state)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!profileUserUuid || !userStore.phoneNumber) return

    let cancelled = false
    goodfellasApi
      .get(`/evaluation/resolve/profile/${profileUserUuid}`)
      .then((res) => {
        if (cancelled) return
        const profile = res.data as {
          profileUserUuid: string
          phoneDigits: string
          name: string
          belt?: string
          degree?: number
          type?: string
        }
        const fighterBelt =
          normalizeBeltColor(profile.belt) || (Belt.Colors.White as string)
        const oneRating = {
          fighterPhone: profile.phoneDigits,
          fighterName: profile.name || profile.phoneDigits,
          fighterBelt,
          fighterBeltDegree: profile.degree ?? 0,
          fighterType: profile.type || FighterType.STUDENT,
          pointsLeft: DEFAULT_POINTS_LEFT,
          gameStyle: 0,
          personalSkills: defaultPersonalSkills(),
          gameSkills: defaultGameSkills(),
          isRated: false,
        }
        feedbackStore.feedbackActions.setFeedback({
          ...feedbackStore,
          phoneNumber: userStore.phoneNumber,
          date: new Date().toISOString(),
          name: userStore.name ?? '',
          belt: userStore.belt ?? '',
          degree: userStore.degree ?? 0,
          type: userStore.type ?? '',
          ratings: [oneRating],
          index: 0,
          feedbackActions: feedbackStore.feedbackActions,
        })
        feedbackStore.feedbackActions.setError(null)
        setLoaded(true)
      })
      .catch((err) => {
        if (cancelled) return
        setError(
          err?.response?.data?.error ??
            err?.message ??
            'Não foi possível carregar o perfil.',
        )
      })

    return () => {
      cancelled = true
    }
  }, [
    profileUserUuid,
    userStore.phoneNumber,
    userStore.name,
    userStore.belt,
    userStore.degree,
    userStore.type,
  ])

  if (error) {
    return (
      <div className="rounded-xl border border-amber-400/40 bg-amber-400/15 px-4 py-3 text-white/90">
        <p>{error}</p>
      </div>
    )
  }

  if (!loaded) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-white/90">
        <p>{t('evaluation.evaluation', 'Avaliação')} por perfil</p>
        <p className="text-sm text-white/70 mt-1">
          Carregando formulário de avaliação…
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-row justify-center gap-4">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <FightersCards
        submitToGlobal
        profileUserUuidForGlobal={profileUserUuid}
      />
    </div>
  )
}
